import { Avatar, Box, Typography } from '@mui/material';
import defaultUserAvatar from '../../assets/default-user-avatar.png';
import rapcardIconSrc from '../../assets/profile-drawer/rapcard-icon.png';
import rapcardOuterBgSrc from '../../assets/profile-drawer/rapcard-outer-bg.png';
import subscriptionBadgeSrc from '../../assets/profile-drawer/subscription-badge.png';
import { getPremiumSubscriptionDisplay } from '../../services/profileService';
import { getUserProfile } from '../../storage/userProfileStorage';

const helloTextSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.5px',
  color: '#fff',
};

const usernameTextSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: 16,
  lineHeight: '24px',
  letterSpacing: '0.8px',
  color: '#fff',
};

/** Figma 11059:2866 — Captions & Helpers */
const activeTextSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontSize: 12,
  lineHeight: '20px',
  letterSpacing: '0.8px',
  color: '#02ca0c',
  whiteSpace: 'nowrap',
};

const rapCardButtonLabelSx = {
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  fontSize: 14,
  lineHeight: '20px',
  letterSpacing: '0.5px',
  color: '#fff',
  whiteSpace: 'nowrap',
};

function getAvatarSrc(profile) {
  return (
    profile?.profilePic ||
    profile?.avatarUrl ||
    profile?.avatar ||
    profile?.profileImage ||
    profile?.imageUrl ||
    null
  );
}

function getDisplayName(profile) {
  return (
    profile?.userName ||
    profile?.username ||
    profile?.name ||
    profile?.email ||
    profile?.phone ||
    'Guest'
  );
}

function ProfileGreeting() {
  const profile = getUserProfile();

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={helloTextSx}>Hello,</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography sx={usernameTextSx}>{getDisplayName(profile)}</Typography>
        <SubscriptionBadgeIcon size={20} />
      </Box>
    </Box>
  );
}

/** Figma 11059:2870 */
function SubscriptionBadgeIcon({ size = 32 }) {
  return (
    <Box
      sx={{
        position: 'relative',
        width: size,
        height: size,
        flexShrink: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        component="img"
        src={subscriptionBadgeSrc}
        alt=""
        aria-hidden="true"
        sx={{
          position: 'absolute',
          height: '99.65%',
          width: '149.34%',
          maxWidth: 'none',
          left: '-24.67%',
          top: '7.64%',
          display: 'block',
          pointerEvents: 'none',
        }}
      />
    </Box>
  );
}

/** Figma 11059:2868 — Body */
function PassTierLabel({ label }) {
  return (
    <Typography
      sx={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 500,
        fontSize: 14,
        lineHeight: '20px',
        letterSpacing: '0.5px',
        color: '#fff',
        width: 73,
        height: 20,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  );
}

/** Figma 11059:2869 — Label */
function PassEndDateLabel({ label }) {
  return (
    <Typography
      sx={{
        fontFamily: 'Inter, sans-serif',
        fontWeight: 400,
        fontSize: 10,
        lineHeight: '12px',
        letterSpacing: 0,
        color: '#fff',
        textAlign: 'center',
        width: 106,
        height: 12,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Typography>
  );
}

/** Figma 11059:2867 */
function PassInfoGroup({ passTier, passEndDate }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: 106,
        height: 32,
        flexShrink: 0,
      }}
    >
      <PassTierLabel label={passTier} />
      <PassEndDateLabel label={passEndDate} />
    </Box>
  );
}

/** Figma 11059:2865 */
function ActiveStatusLabel({ label }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '70px',
        height: '28px',
        textAlign: 'center',
        flexShrink: 0,
        ml: 'auto',
        borderRadius: '5px',
        border: '0.5px solid #02CA0C',
        background: '#003005',
        boxShadow: '0 0 8px 0 rgba(0, 0, 0, 0.55) inset',
        justifyContent: 'center',
      }}
    >
      <Typography sx={{ ...activeTextSx, textAlign: 'center' }}>{label}</Typography>
    </Box>
  );
}

/** Figma 11059:2855 */
function SubscriptionStatusBar({ passTier, passStatus, passEndDate }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        width: 360,
        height: 52,
        borderRadius: '12px',
        border: '0.4px solid #02ca0c',
        bgcolor: '#004102',
        boxShadow: 'inset -2px -2px 15px rgba(0, 0, 0, 0.5)',
        px: '10px',
        py: '10px',
        position: 'absolute',
        left: 10,
        bottom: 16,
        overflow: 'hidden',
      }}
    >
      <SubscriptionBadgeIcon />
      <PassInfoGroup passTier={passTier} passEndDate={passEndDate} />
      <ActiveStatusLabel label={passStatus} />
    </Box>
  );
}

function MyRapCardButton() {
  return (
    <Box
      component="button"
      type="button"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '5px',
        width: 137,
        height: 38,
        borderRadius: '100px',
        border: '1px solid #ff00f2',
        p: '8px',
        bgcolor: 'rgba(0, 0, 0, 0.54)',
        boxShadow: 'inset 0px 0px 4px rgba(0, 0, 0, 0.65)',
        cursor: 'pointer',
        color: '#fff',
        flexShrink: 0,
        '&:hover': { opacity: 0.92 },
      }}
    >
      <Box
        component="img"
        src={rapcardIconSrc}
        alt=""
        sx={{ width: 24, height: 24, objectFit: 'contain', display: 'block', flexShrink: 0 }}
      />
      <Typography component="span" sx={rapCardButtonLabelSx}>
        My RapCard
      </Typography>
    </Box>
  );
}

export default function ProfileRapCard({ onClick }) {
  const profile = getUserProfile();
  const avatarSrc = getAvatarSrc(profile) ?? defaultUserAvatar;
  const subscription = getPremiumSubscriptionDisplay(profile);
  const hasSubscription = Boolean(subscription);

  return (
      <Box
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
        sx={{
          borderRadius: '36px',
          backgroundImage: `url(${rapcardOuterBgSrc})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          p: '10px',
          mb: 3,
          mx: 'auto',
          width: '100%',
          maxWidth: 400,
          minHeight: hasSubscription ? 174 : 'auto',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'opacity 0.15s ease',
          '&:hover': onClick ? { opacity: 0.92 } : undefined,
        }}
      >
        <Box
          sx={{
            borderRadius: '26px',
            border: '2px dashed #f201ff',
            position: 'relative',
            overflow: 'hidden',
            px: 1.5,
            pt: 1.5,
            pb: hasSubscription ? '42px' : 1.5,
            width: '100%',
            minHeight: hasSubscription ? 154 : 'auto',
            height: hasSubscription ? 154 : 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Avatar
              src={avatarSrc}
              alt={getDisplayName(profile)}
              sx={{
                width: 50,
                height: 50,
                border: '1px solid #ffceca',
                bgcolor: '#ffceca',
                flexShrink: 0,
                '& .MuiAvatar-img': {
                  objectFit: 'cover',
                  objectPosition: 'center top',
                },
              }}
            />
            <ProfileGreeting />
            <MyRapCardButton />
          </Box>

          {hasSubscription ? (
            <SubscriptionStatusBar
              passTier={subscription.passTier}
              passStatus={subscription.passStatus}
              passEndDate={subscription.passEndDate}
            />
          ) : null}
        </Box>
      </Box>
  );
}
